/*
 * Virtuele paginering voor de collectiepagina.
 *
 * Het thema rendert de volledige collectie in één keer (Shopify's paginate levert
 * tot 250 producten), al gesorteerd met de bestelbare producten vooraan. Deze
 * module knipt die lijst client-side in pagina's, zodat de volgorde doorloopt over
 * alle pagina's heen zonder dat er iets aan de collectie in Shopify verandert.
 *
 * Acht collecties passen niet in die 250 en krijgen van Shopify meerdere Liquid-
 * pagina's. De nummers lopen dan gewoon door: dit blok begint bij `before` en het
 * totaal staat in `total`. Een knop binnen dit blok is een hash en wordt hier
 * afgehandeld; een knop uit een ander blok is een echte link naar ?page=N.
 *
 * Zonder JavaScript gebeurt er niets: je ziet alle producten in de juiste volgorde
 * en, op die acht collecties, Shopify's eigen paginering — dat is de bewuste
 * terugvaloptie, geen ongeluk.
 */
(function () {
  'use strict';

  var HIDDEN = 'vpage-hidden';

  // De hashchange-luisteraar wordt één keer opgehangen. init() draait namelijk opnieuw
  // na elke filterwijziging, en een luisteraar per keer zou blijven verwijzen naar de
  // kaarten van de vórige grid.
  var onHash = null;

  function num(el, attr, fallback) {
    var value = parseInt(el.getAttribute(attr) || '', 10);
    return isNaN(value) ? fallback : value;
  }

  function pagesFor(grid, size) {
    if (!size || size < 1) return null;

    // Elk direct kind is óf een productkaart óf een teaser. Teasers horen bij de
    // pagina waar ze tussen staan, dus ze erven het paginanummer van de kaart ervoor.
    var pages = [];
    var current = [];
    var cardsOnPage = 0;

    Array.prototype.forEach.call(grid.children, function (el) {
      var isCard = el.hasAttribute('data-product-card');
      if (isCard && cardsOnPage === size) {
        pages.push(current);
        current = [];
        cardsOnPage = 0;
      }
      current.push(el);
      if (isCard) cardsOnPage++;
    });
    if (current.length) pages.push(current);
    return pages;
  }

  function scrollToGrid() {
    var anchor = document.getElementById('ProductGridContainer');
    if (!anchor) return;
    var top = anchor.getBoundingClientRect().top + window.pageYOffset - 24;
    if (window.pageYOffset > top) window.scrollTo({ top: top, behavior: 'smooth' });
  }

  function readPage(before) {
    var m = /(?:^|[?&#])vpagina=(\d+)/.exec(window.location.hash + window.location.search);
    return m ? parseInt(m[1], 10) : before + 1;
  }

  /*
   * De nummers die in de balk komen. Past de hele collectie in één Liquid-blok —
   * dat geldt voor 126 van de 134 collecties — dan blijven ze allemaal staan, precies
   * zoals het was. Daarboven zouden het er 47 worden, dus wordt er ingekort tot het
   * eerste en laatste nummer, de buren van de huidige pagina, en het eerste nummer
   * van elk Liquid-blok. Die laatste horen erbij omdat ze de enige echte links zijn:
   * zonder die knoppen kom je alleen met achttien keer 'volgende' in het tweede blok.
   */
  function numbersFor(total, current, perBlock) {
    var out = [];
    var i;

    if (total <= perBlock) {
      for (i = 1; i <= total; i++) out.push(i);
      return out;
    }

    var keep = {};
    keep[1] = true;
    keep[total] = true;
    for (i = current - 2; i <= current + 2; i++) {
      if (i >= 1 && i <= total) keep[i] = true;
    }
    for (i = 1; i <= total; i += perBlock) keep[i] = true;

    Object.keys(keep)
      .map(Number)
      .sort(function (a, b) {
        return a - b;
      })
      .forEach(function (n) {
        out.push(n);
      });
    return out;
  }

  function build(container, state, current) {
    if (state.total < 2) {
      container.classList.add('hidden');
      document.documentElement.classList.remove('js-vpage');
      return;
    }
    container.classList.remove('hidden');

    // Shopify's eigen balk zit nu in deze nummers verwerkt en mag weg. Het gaat via
    // een klasse op <html> omdat facets.js die balk na een filterwijziging opnieuw
    // uit de server-HTML zet — een klasse op het element zelf overleeft dat niet.
    document.documentElement.classList.add('js-vpage');

    var prev = container.querySelector('[data-vpage-prev]');
    var next = container.querySelector('[data-vpage-next]');
    var list = container.querySelector('[data-vpage-list]');
    if (!list) return;

    // Een nummer in dit blok is een hash: geen serververkeer, de kaarten staan er al.
    // Een nummer uit een ander blok van 250 moet wél opgehaald worden en wordt dus een
    // gewone link, met filters en sortering uit de huidige URL erbij.
    function hrefFor(n) {
      var local = n - state.before;
      if (local >= 1 && local <= state.pages) return '#vpagina=' + n;
      var url = new URL(window.location.href);
      var liquidPage = Math.floor((n - 1) / state.perBlock) + 1;
      if (liquidPage > 1) url.searchParams.set('page', String(liquidPage));
      else url.searchParams.delete('page');
      url.hash = 'vpagina=' + n;
      return url.toString();
    }

    list.innerHTML = '';
    var numbers = numbersFor(state.total, current, state.perBlock);
    numbers.forEach(function (n, i) {
      if (i > 0 && n !== numbers[i - 1] + 1) {
        var gap = document.createElement('li');
        gap.className = 'h-10 flex items-center justify-center text-dark-blue text-xs md:text-sm font-bold';
        gap.textContent = '…';
        list.appendChild(gap);
      }
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.textContent = String(n);
      a.setAttribute('title', String(n));
      a.href = hrefFor(n);
      a.className =
        'h-10 w-10 rounded-[4px] border flex items-center justify-center text-dark-blue text-xs md:text-sm font-bold transition duration-300 ' +
        (n === current ? 'border-dark-blue' : 'border-grey hover:border-dark-blue');
      li.appendChild(a);
      list.appendChild(li);
    });

    [[prev, current - 1], [next, current + 1]].forEach(function (pair) {
      var el = pair[0];
      var target = pair[1];
      if (!el) return;
      var enabled = target >= 1 && target <= state.total;
      el.classList.toggle('opacity-40', !enabled);
      el.setAttribute('href', enabled ? hrefFor(target) : 'javascript:;');
    });
  }

  function init(root) {
    onHash = null;
    var grid = (root || document).querySelector('#product-grid[data-vpage-size]');
    if (!grid) return;
    var container = document.querySelector('[data-vpage-nav]');
    if (!container) return;

    var size = num(grid, 'data-vpage-size', 0);
    var pages = pagesFor(grid, size);
    if (!pages || !pages.length) {
      container.classList.add('hidden');
      document.documentElement.classList.remove('js-vpage');
      return;
    }

    var before = num(grid, 'data-vpage-before', 0);
    var state = {
      before: before,
      pages: pages.length,
      perBlock: num(grid, 'data-vpage-per-block', pages.length) || pages.length,
      // Bij een filterwijziging haalt facets.js altijd Liquid-pagina 1 op, dus dan
      // klopt het totaal uit de verse markup ook weer met wat er in de grid staat.
      total: num(grid, 'data-vpage-total', 0) || pages.length
    };

    // Een klik toont de pagina al vóór de hash verandert; het hashchange dat daarna
    // nog volgt hoeft dan niets meer te doen. Zo blijft de grid ook stil tijdens de
    // scroll die de klik inzet.
    var shown = 0;

    function show(n) {
      var page = Math.min(Math.max(n - state.before, 1), state.pages);
      if (page === shown) return;
      shown = page;
      pages.forEach(function (items, i) {
        items.forEach(function (el) {
          el.classList.toggle(HIDDEN, i !== page - 1);
        });
      });

      // De laatste kaart van een oneven rij krijgt op mobiel een eigen opmaak.
      // Tellen doen we over álle kaart-brede vakken, dus inclusief het adviesblok
      // en de teasers — die staan op mobiel gewoon in de rij. De USP-balk telt niet
      // mee: die is col-span-12 en op mobiel verborgen. Alleen productkaarten tellen
      // gaf een even getal terwijl de rij oneven was, en dus een gat.
      var visible = pages[page - 1].filter(function (el) {
        return el.className.indexOf('col-span-6') !== -1;
      });
      Array.prototype.forEach.call(grid.querySelectorAll('.last-odd-product'), function (el) {
        el.classList.remove('last-odd-product');
      });
      if (visible.length % 2 === 1) {
        visible[visible.length - 1].classList.add('last-odd-product');
      }

      build(container, state, state.before + page);
      grid.setAttribute('data-vpage-current', String(state.before + page));
    }

    container.onclick = function (event) {
      var link = event.target.closest('a[href]');
      if (!link) return;
      // Alleen de knoppen binnen dit blok worden hier afgehandeld; een link naar een
      // ander blok van 250 moet gewoon navigeren.
      var href = link.getAttribute('href') || '';
      if (href.charAt(0) !== '#') return;
      var match = /vpagina=(\d+)/.exec(href);
      if (!match) return;
      var n = parseInt(match[1], 10);
      if (!n) return;
      event.preventDefault();

      // Eerst wisselen, dán scrollen. Andersom begon de scroll nog op de lange
      // pagina 1: hashchange vuurt pas daarna, en zodra die de kaarten verborg
      // kromp het document en trok de browser de scrollpositie recht — wat de
      // lopende animatie afkapte. Op een pagina van één rij bleef je zo onder die
      // rij hangen, bij de balk waar je net op klikte.
      show(n);

      // Let op: Google Tag Manager zet op deze pagina een globale `history` die een
      // array is, waardoor een kale `history.replaceState(...)` stukloopt. Altijd
      // via window.location werken en globals expliciet met window. aanspreken.
      var wanted = '#vpagina=' + n;
      if (window.location.hash !== wanted) window.location.hash = wanted;
      scrollToGrid();
    };

    show(readPage(state.before));

    onHash = function () {
      show(readPage(state.before));
    };
  }

  window.addEventListener('hashchange', function () {
    if (onHash) onHash();
  });

  document.addEventListener('DOMContentLoaded', function () {
    init(document);
  });

  // De facetten vervangen de hele grid via de Section Rendering API; daarna moet
  // de paginering opnieuw opgebouwd worden over de nieuwe kaarten.
  document.addEventListener('godu:collection-updated', function () {
    init(document);
  });
})();
